import { Component, HostListener, inject, DestroyRef, Input, OnInit } from '@angular/core';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { DocumentState } from '@app/core/enums/document/document-state.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Asset } from '@app/core/model/entities/asset/asset';
import { ManualWorkflowViewEnum } from '@app/core/model/entities/workflow/workflow';
import { PicturesService } from '@app/features/main/views/assets/asset-sheet/pictures/pictures.service';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { AssetModalService } from '@app/features/main/views/assets/modals/asset-modal.service';
import { getPictureComparator } from '@app/shared/extra/utils';
import { AssetNameReset } from '@app/shared/store/reducers/asset-name.actions';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FileService } from '@services/file.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { Image } from '@tbmaestro/ngx-picture-carousel';
import { Observable, of } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type Tab = {
  name: string,
  permission: PermissionEnum,
  key: string,
  getTypeConditionToView: (modules: string[]) => boolean
};

@Component({
    selector: 'asset-sidebar',
    templateUrl: './asset-sidebar.component.html',
    styleUrls: ['./asset-sidebar.component.scss'],
    providers: [AssetModalService],
    standalone: false
})
export class AssetSidebarComponent implements OnInit {
  private readonly assetsService = inject(AssetsService);
  private readonly pictureService = inject(PicturesService);
  private readonly fileService = inject(FileService);
  protected readonly analyticsService = inject(AnalyticsService);
  private readonly store = inject<Store<any>>(Store);
  private readonly appManager = inject(AppManager);
  protected readonly snackbarManager = inject(SnackbarManager);
  protected readonly accessManager = inject(AccessManager);
  protected readonly translate = inject(TranslateService);
  protected readonly appConfig = inject(AppConfig);
  private readonly assetModalService = inject(AssetModalService);

  protected readonly EntityType = EntityTypeEnum;
  protected readonly ManualWorkflowViewEnum = ManualWorkflowViewEnum;
  public Permission = PermissionEnum;

  @Input() public showDeleteButton = true;

  public tabs: Tab[] = [];
  protected assetTabs: {
    name: string,
    key: string
  }[] = [];
  public image: Image;
  public asset: Asset;

  protected destroyRef = inject(DestroyRef);

  constructor() {
    this.tabs = [
      <Tab>{
        name: 'MENU.ASSET_DASHBOARD',
        permission: PermissionEnum.VIEW_ASSET,
        key: 'dashboard',
        getTypeConditionToView: () => true
      },
      <Tab>{
        name: 'MENU.ASSET_FEATURES',
        permission: PermissionEnum.VIEW_ASSET,
        key: 'features',
        getTypeConditionToView: () => true
      },
      <Tab>{
        name: 'MENU.ASSET_INDICATORS',
        permission: PermissionEnum.VIEW_ASSET,
        key: 'indicators',
        getTypeConditionToView: () => true
      },
      <Tab>{
        entityType: EntityTypeEnum.CRV,
        name: 'MENU.ASSET_CRV',
        permission: PermissionEnum.VIEW_CRV,
        key: 'crv',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.ASSET_CRV)
      },
      <Tab>{
        entityType: EntityTypeEnum.SPACE,
        name: 'MENU.ASSET_SPACES',
        permission: PermissionEnum.VIEW_SPACE,
        key: 'spaces',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.SPACE)
      },
      <Tab>{
        entityType: EntityTypeEnum.EQUIPMENT,
        name: 'MENU.ASSET_EQUIPMENTS',
        permission: PermissionEnum.VIEW_EQUIPMENT,
        key: 'equipments',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.EQUIPMENT)
      },
      <Tab>{
        name: 'MENU.ASSET_PICTURES',
        permission: PermissionEnum.VIEW_ASSET,
        key: 'pictures',
        getTypeConditionToView: () => true
      },
      <Tab>{
        name: 'MENU.ASSET_DOCUMENTS',
        permission: PermissionEnum.VIEW_DOCUMENT,
        key: 'documents',
        getTypeConditionToView: () => true
      },
      <Tab>{
        name: 'MENU.ASSET_CHECKS',
        permission: PermissionEnum.VIEW_CHECK,
        key: 'checks',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.CHECK)
      },
      <Tab>{
        name: 'MENU.ASSET_OCCUPANTS',
        permission: PermissionEnum.VIEW_OCCUPANT,
        key: 'occupants',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.LEASE)
      },
      <Tab>{
        name: 'MENU.ASSET_CONTRACTS',
        permission: PermissionEnum.VIEW_CONTRACT,
        key: 'contracts',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.CONTRACT)
      },
      <Tab>{
        name: 'MENU.ASSET_WORKS',
        permission: PermissionEnum.VIEW_WORK,
        key: 'works',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.WORK)
      },
      <Tab>{
        name: 'MENU.ASSET_PROJECTS',
        permission: PermissionEnum.VIEW_PROJECT,
        key: 'projects',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.PROJECT)
      },
      <Tab>{
        name: 'MENU.ASSET_INVESTMENTS',
        permission: PermissionEnum.VIEW_INVESTMENT,
        key: 'investments',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.INVESTMENT)
      },
      <Tab>{
        entityType: EntityTypeEnum.VIRTUAL_TOUR,
        name: 'MENU.ASSET_VIRTUAL_TOUR',
        permission: PermissionEnum.VIEW_VIRTUAL_TOUR,
        key: 'virtual-tour',
        getTypeConditionToView: modules => modules.includes(EntityTypeEnum.VIRTUAL_TOUR)
      }
    ]
      // Remove tabs which the user can't access
      .filter(tab => this.accessManager.hasAccess(tab.permission));
  }

  /**
   * Listen for the AssetService's Observable that signals when the side panel needs to open or close.
   */
  public ngOnInit(): void {
    this.assetsService.sidePanelToggle$
      .pipe(
        switchMap(assetData => {
          if (assetData) {
            // There is a Asset to display, listen to external changes to this Asset to update the side panel data
            return this.assetsService.assetUpdated$
              .pipe(
                filter(updatedAsset => updatedAsset.id === assetData.id),
                startWith(assetData),
                takeUntilDestroyed(this.destroyRef)
              );
          } else {
            // There is no Asset to display, the side panel is closed, just propagate the null value
            return of(assetData);
          }
        }),
        tap(() => {
          // Unload current Asset
          if (this.appManager.currentEntity instanceof Asset) {
            this.appManager.unloadCurrentEntity();
          }
        }),
        filter(assetSidebarData => !!assetSidebarData),
        tap(asset => {
          // Set current Asset
          this.asset = asset;
          this.appManager.currentEntity = asset;
          const assetTypeModules: string[] = this.asset.computedProperties?.['typeModules'] ?? [];
          this.assetTabs = this.tabs.filter((tab) => {
            return tab.getTypeConditionToView(assetTypeModules);
          });
          this.store.dispatch(new AssetNameReset({
            currentValue: this.asset.name,
            originalValue: this.asset.name
          }));
        }),
        switchMap(asset => {
          // Reset image
          this.image = null;
          return this.loadAssetPicture(asset.id);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Load the asset's picture
   * @param assetId
   * @return An observable emitting when the request complete
   */
  private loadAssetPicture(assetId: string): Observable<any> {
    return this.pictureService.loadData(assetId).pipe(
      switchMap(pictures => {
        // Filter archived documents out and place main picture at first position
        const picture = pictures.filter(picture => picture.state === DocumentState.ACTIVE)
          .sort(getPictureComparator(this.asset.properties['mainPictureDocumentId']))
          .first();
        if (picture) {
          const documentId = picture.id;
          return this.fileService.getDocumentFileUrls([documentId])
            .pipe(
              map(urls => this.image = {
                id: picture.id,
                url: urls[picture.id]
              })
            );
        }

        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  /**
   * Close the asset side panel.
   */
  public closeAssetSidebar(): void {
    this.assetsService.closeAssetSidePanel();
  }

  @HostListener('keyup', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeAssetSidebar();
    }
  }

  /**
   * Navigate to the asset sheet tab.
   * @param tabName the tab to navigate to.
   */
  public async navigateToAssetSheet(tabName: string): Promise<void> {
    this.closeAssetSidebar();
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.SHEET,
      EntityTypeEnum.ASSET,
      this.asset.id
    );
    await this.assetsService.navigateToAssetSheet(this.asset.id, tabName);
  }

  /**
   * Show confirmation dialog then delete the Asset.
   */
  public deleteAsset(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.ASSET,
      this.asset.id
    );
    this.assetModalService.openDeleteAssetsDialog([this.asset])
      .pipe(
        filter(success => success),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.ASSET_DELETED')));
  }
}
